// Mounts the OTA client. Renders nothing.
//
// Mount ONCE, at the app root, on both UI shells — an installed Android build
// serves the mobile UI, but the desktop shell would run inside the same APK if
// someone forced it, and a double mount would run two update checks.
//
// All the decision logic lives in core/ota/otaClient.js; this only owns the
// lifecycle: call notifyAppReady on boot, check once, then re-check whenever the
// app comes back to the foreground (sessions here can stay open for days on a
// wall-mounted tablet, so launch-only checking would never see an update).

import { useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import { checkForUpdate, loadUpdater, otaSupported } from '../../core/ota/otaClient';

export default function OtaUpdater() {
  useEffect(() => {
    if (!otaSupported()) return undefined;

    let cancelled = false;
    let resumeHandle;

    (async () => {
      try {
        const CapacitorUpdater = await loadUpdater();
        if (cancelled) return;

        // MANDATORY — tells the plugin this bundle booted fine and cancels the
        // rollback watchdog. Without it every applied update reverts.
        await CapacitorUpdater.notifyAppReady();

        await checkForUpdate(CapacitorUpdater);
        if (cancelled) return;

        const h = await CapApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) checkForUpdate(CapacitorUpdater);
        });
        if (cancelled) { h.remove(); return; }
        resumeHandle = h;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[OTA] updater init skipped:', (err && err.message) || err);
      }
    })();

    return () => {
      cancelled = true;
      if (resumeHandle) { try { resumeHandle.remove(); } catch (e) { /* noop */ } }
    };
  }, []);

  return null;
}
